import React, { useEffect, useState } from 'react'
import StockDeltaChart from '../charts/StockDeltaChart'
import { useLiveStockPrices } from '../../hooks/useLiveStockPrices'

interface StockHistory {
  symbol: string
  has_weekly_options: boolean
  missing_weeks: number[]
  weekly_data: Array<{
    week_offset: number
    net_delta: number
    signal: string
    price: number
    dollar_delta: number
    expiry_date: string
  }>
}

interface TopMoverData {
  Expiry_Date: string
  Top_Bullish_Symbol: string
  Top_Bullish_Delta: number
  Top_Bullish_Dollar_Delta: number
  Top_Bullish_Price: number
  Top_Bearish_Symbol: string
  Top_Bearish_Delta: number
  Top_Bearish_Dollar_Delta: number
  Top_Bearish_Price: number
}

interface TopMoversSectionProps {
  weekOffset: number
}

const TopMoversSection: React.FC<TopMoversSectionProps> = ({ weekOffset }) => {
  const [moversData, setMoversData] = useState<TopMoverData | null>(null)
  const [stockHistories, setStockHistories] = useState<Record<string, StockHistory>>({})
  const [loading, setLoading] = useState(true)
  
  // Get symbols for price fetching (Note: API currently returns static fallback prices)
  const symbols = moversData ? [moversData.Top_Bullish_Symbol, moversData.Top_Bearish_Symbol] : []
  const { quotes: liveQuotes } = useLiveStockPrices(symbols, 300000) // Check every 5 minutes (but returns static prices)

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load top movers data
        const moversResponse = await fetch('/data/weekly_top_movers.json')
        if (!moversResponse.ok) {
          throw new Error(`Failed to fetch movers data: ${moversResponse.status}`)
        }
        const moversData = await moversResponse.json()
        
        // Format data to match expected structure
        const result = { data: moversData }
        
        if (result.data && result.data[weekOffset]) {
          setMoversData(result.data[weekOffset] as TopMoverData)
        }

        // Load stock histories
        const historyResponse = await fetch('/data/stock_histories.json')
        const histories = await historyResponse.json()
        setStockHistories(histories)
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [weekOffset])

  const formatDelta = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(value))
  }

  const formatDollarDelta = (value: number) => {
    const absValue = Math.abs(value)
    if (absValue >= 1e9) {
      return `$${(absValue / 1e9).toFixed(1)}B`
    } else if (absValue >= 1e6) {
      return `$${(absValue / 1e6).toFixed(1)}M`
    } else if (absValue >= 1e3) {
      return `$${(absValue / 1e3).toFixed(1)}K`
    }
    return `$${absValue.toFixed(0)}`
  }

  if (loading || !moversData) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const bullishHistory = stockHistories[moversData.Top_Bullish_Symbol]
  const bearishHistory = stockHistories[moversData.Top_Bearish_Symbol]
  const rawBullishQuote = liveQuotes.get(moversData.Top_Bullish_Symbol)
  const rawBearishQuote = liveQuotes.get(moversData.Top_Bearish_Symbol)
  
  // Only use live quotes if they have a real price (> 0), otherwise fall back to data file
  const bullishQuote = rawBullishQuote && rawBullishQuote.price > 0 ? rawBullishQuote : null
  const bearishQuote = rawBearishQuote && rawBearishQuote.price > 0 ? rawBearishQuote : null

  return (
    <div className="space-y-8">
      {/* Section Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Today's Top Stock Movers for {weekOffset === 0 ? 'This Week' : `Week ${weekOffset}`}
        </h2>
        <p className="text-gray-600">
          Based on today's options flow analysis - rankings update daily as market conditions change
        </p>
        <p className="text-sm text-gray-500 mt-2">
          <span className="font-medium">Why we show weekly projections:</span> Daily noise is filtered out to reveal true institutional positioning
        </p>
        <p className="text-sm text-gray-500 mt-1">
          Charts below display 6-week options flow trends for each stock
        </p>
      </div>

      {/* Info Box */}
      <div className="max-w-4xl mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How Top Movers Are Selected:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li>Rankings are calculated fresh each day based on current options flow</li>
              <li>We analyze this week's expiring options across all 500 S&P stocks</li>
              <li>Stocks shown had the strongest signals in today's analysis</li>
              <li>Tomorrow's top movers may be completely different stocks</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Top Movers Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Bullish Mover */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-lg p-6 border-2 border-green-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Top Bullish</h3>
            <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              FREE
            </span>
          </div>
          
          <div className="mb-4">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-gray-900">{moversData.Top_Bullish_Symbol}</span>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    ${bullishQuote?.price.toFixed(2) || moversData.Top_Bullish_Price?.toFixed(2) || '0.00'}
                  </span>
                  {bullishQuote ? (
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                      CURRENT
                    </span>
                  ) : (
                    <span className="bg-gray-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                      STATIC
                    </span>
                  )}
                </div>
                {bullishQuote && (
                  <div className="flex items-center gap-1">
                    {bullishQuote.change >= 0 ? (
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className={`text-sm font-medium ${
                      bullishQuote.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {bullishQuote.change >= 0 ? '+' : ''}{bullishQuote.change.toFixed(2)}
                    </span>
                    <span className={`text-sm ${
                      bullishQuote.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ({bullishQuote.changePercent >= 0 ? '+' : ''}{bullishQuote.changePercent.toFixed(2)}%)
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-2 space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">This Week's Signal Strength</span>
                <span className="text-green-700 font-semibold">
                  +{formatDelta(moversData.Top_Bullish_Delta)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Dollar Impact</span>
                <span className="text-green-700 font-semibold">
                  +{formatDollarDelta(moversData.Top_Bullish_Price * moversData.Top_Bullish_Delta)}
                </span>
              </div>
            </div>
          </div>

          {/* Chart */}
          {bullishHistory ? (
            <div className="mt-4 bg-white rounded-lg p-3">
              <StockDeltaChart 
                symbol={moversData.Top_Bullish_Symbol}
                data={bullishHistory.weekly_data.slice(0, 6)} // Show weeks 0-5
                height={180}
              />
              {!bullishHistory.has_weekly_options && (
                <p className="text-xs text-orange-600 mt-2 text-center">
                  ⚠️ Limited weekly options available
                </p>
              )}
            </div>
          ) : (
            <div className="mt-4 bg-white rounded-lg p-3 text-center text-gray-500 text-sm py-8">
              <p>📊 Chart data loading...</p>
              <p className="text-xs mt-1">Detailed options flow chart will be available shortly</p>
            </div>
          )}
        </div>

        {/* Bearish Mover */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-lg p-6 border-2 border-red-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Top Bearish</h3>
            <span className="bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              FREE
            </span>
          </div>
          
          <div className="mb-4">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-gray-900">{moversData.Top_Bearish_Symbol}</span>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    ${bearishQuote?.price.toFixed(2) || moversData.Top_Bearish_Price?.toFixed(2) || '0.00'}
                  </span>
                  {bearishQuote ? (
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                      CURRENT
                    </span>
                  ) : (
                    <span className="bg-gray-500 text-white text-xs font-bold px-2 py-0.5 rounded">
                      STATIC
                    </span>
                  )}
                </div>
                {bearishQuote && (
                  <div className="flex items-center gap-1">
                    {bearishQuote.change >= 0 ? (
                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className={`text-sm font-medium ${
                      bearishQuote.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {bearishQuote.change >= 0 ? '+' : ''}{bearishQuote.change.toFixed(2)}
                    </span>
                    <span className={`text-sm ${
                      bearishQuote.change >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      ({bearishQuote.changePercent >= 0 ? '+' : ''}{bearishQuote.changePercent.toFixed(2)}%)
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-2 space-y-1">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">This Week's Signal Strength</span>
                <span className="text-red-700 font-semibold">
                  -{formatDelta(Math.abs(moversData.Top_Bearish_Delta))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Dollar Impact</span>
                <span className="text-red-700 font-semibold">
                  -{formatDollarDelta(Math.abs(moversData.Top_Bearish_Price * moversData.Top_Bearish_Delta))}
                </span>
              </div>
            </div>
          </div>

          {/* Chart */}
          {bearishHistory ? (
            <div className="mt-4 bg-white rounded-lg p-3">
              <StockDeltaChart 
                symbol={moversData.Top_Bearish_Symbol}
                data={bearishHistory.weekly_data.slice(0, 6)} // Show weeks 0-5
                height={180}
              />
              {!bearishHistory.has_weekly_options && (
                <p className="text-xs text-orange-600 mt-2 text-center">
                  ⚠️ Limited weekly options available
                </p>
              )}
            </div>
          ) : (
            <div className="mt-4 bg-white rounded-lg p-3 text-center text-gray-500 text-sm py-8">
              <p>📊 Chart data loading...</p>
              <p className="text-xs mt-1">Detailed options flow chart will be available shortly</p>
            </div>
          )}
        </div>
      </div>

      {/* Price Update Notice - Removed misleading live update claim */}

      {/* Last Updated Notice */}
      <div className="text-center text-sm text-gray-500 mt-4">
        <p>
          Week 0 expiry: {moversData.Expiry_Date ? (() => {
            const [year, month, day] = moversData.Expiry_Date.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return date.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric',
              year: 'numeric'
            })
          })() : 'N/A'}
        </p>
        <p className="text-xs mt-1">Data updates weekdays after market close</p>
      </div>

      {/* Premium Teaser */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white text-center">
        <h3 className="text-xl font-semibold mb-2">
          Want to See All 500+ Ranked Stocks?
        </h3>
        <p className="mb-4">
          Premium members get access to complete rankings with detailed analysis for all 500+ stocks
        </p>
        <div className="bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-2 rounded-full inline-block">
          COMING SOON - Get Notified!
        </div>
      </div>
    </div>
  )
}

export default TopMoversSection